import type { LegalDocument } from '../types/content';

/**
 * Legal pages are drafted as substantive, reviewable placeholders. Every one is
 * flagged as requiring qualified legal review before publication.
 */
export const legalDocuments: LegalDocument[] = [
  {
    slug: 'privacy-policy',
    title: 'Privacy Policy',
    lastReviewed: '2026-08-01',
    summary:
      'How we collect, use, store and share personal information about candidates, employer representatives and website visitors, and the rights you hold over it.',
    requiresLegalReview: true,
    sections: [
      {
        heading: 'Information we collect',
        paragraphs: [
          'When you register as an executive we collect the information you choose to provide: your name, professional contact details, location, career history, target roles and preferences, and any documents you upload.',
          'We do not collect and will never ask for account passwords to third-party services, Social Security numbers, government identity numbers, dates of birth, or banking credentials. If any form on this platform appears to request one of these, do not complete it and report it to us immediately.',
          'We collect limited technical information necessary to operate the platform securely, including authentication session data and login attempt records.',
        ],
      },
      {
        heading: 'How we use your information',
        paragraphs: [
          'To assess your candidacy against mandates we have been commissioned to conduct, to communicate with you about your applications and documents, and to operate the platform.',
          'We use an internal matching process to rank potential fit between profiles and mandates. Match scores are a ranking signal reviewed by a consultant. No adverse decision about your candidacy is made by automated means alone.',
          'We send you marketing communications only where you have opted in, and you may withdraw that consent at any time from your notification preferences.',
        ],
      },
      {
        heading: 'Sharing your information with employers',
        paragraphs: [
          'We do not share your identity, your documents or your candidacy with any employer without your recorded, mandate-specific authorization.',
          'Where a client’s identity is temporarily withheld, the authorization request will state that a restriction applies and why, and you may decline. Declining does not affect your standing on the platform or your ability to apply for other opportunities.',
          'Every authorization and every decline is recorded with a timestamp and is visible to you in your consent history.',
        ],
      },
      {
        heading: 'Retention',
        paragraphs: [
          'We retain candidate profiles for the period stated in your consent record, and refresh that consent periodically. You may request deletion at any time.',
          'Certain records — consent records, audit logs and records of privacy requests — are retained for a longer defined period because they evidence our compliance obligations.',
        ],
      },
      {
        heading: 'Your rights',
        paragraphs: [
          'You may request a copy of your data, request correction, withdraw consent, request restriction of processing, or request deletion of your account. Requests can be submitted from your portal privacy settings or through the contact page.',
          'We acknowledge every request and confirm the outcome in writing, with a reference number you can quote.',
        ],
      },
      {
        heading: 'Contact',
        paragraphs: [
          'Privacy enquiries should be directed to the privacy request form on our contact page, which routes to the data protection contact for this organization.',
        ],
      },
    ],
  },
  {
    slug: 'terms-of-use',
    title: 'Terms of Use',
    lastReviewed: '2026-08-01',
    summary: 'The terms governing your use of this website and the candidate and employer portals.',
    requiresLegalReview: true,
    sections: [
      {
        heading: 'Acceptance',
        paragraphs: [
          'By accessing this website or registering for a portal account you agree to these terms. If you do not agree, do not use the platform.',
        ],
      },
      {
        heading: 'Accounts',
        paragraphs: [
          'You are responsible for the accuracy of the information you provide and for maintaining the confidentiality of your credentials.',
          'Employer accounts require verification before they may submit a mandate or view any candidate information. We may decline or suspend an account where verification cannot be completed.',
        ],
      },
      {
        heading: 'Nature of our services',
        paragraphs: [
          'We are a retained executive search firm engaged by client organizations. We do not guarantee employment, interviews, presentation to a client, compensation outcomes or job offers to any candidate under any circumstances.',
          'Opportunities on this platform are labelled to indicate their status. A Proposed Opportunity or Market-Matched Opportunity is not a confirmed vacancy and must not be relied upon as one.',
        ],
      },
      {
        heading: 'Acceptable use',
        paragraphs: [
          'You may not misrepresent your identity or authority, upload malicious content, attempt to access records you are not authorized to view, or use the platform to solicit our candidates or clients.',
        ],
      },
      {
        heading: 'Limitation of liability',
        paragraphs: [
          'This section requires drafting by qualified counsel appropriate to the jurisdictions in which the firm operates and must not be published in placeholder form.',
        ],
      },
    ],
  },
  {
    slug: 'candidate-consent',
    title: 'Candidate Consent',
    lastReviewed: '2026-08-01',
    summary:
      'What you are consenting to when you register, what each consent covers, and how to change or withdraw it.',
    requiresLegalReview: true,
    sections: [
      {
        heading: 'Consent is granular',
        paragraphs: [
          'We record each consent separately rather than as a single bundled agreement. You may grant some and decline others, and you may change any of them at any time.',
        ],
      },
      {
        heading: 'The consents we record',
        paragraphs: [
          'Privacy notice acknowledgement: that you have read how we handle your information. Required to hold an account.',
          'General candidacy: that we may hold your profile and consider you against mandates we conduct.',
          'Named-employer presentation: that we may seek your authorization to present you to a named employer. This is permission to ask, not permission to present.',
          'Confidential mandate consideration: that we may approach you about mandates where the client’s identity is initially withheld.',
          'Marketing communications: that we may send you market insight and career content. Entirely optional.',
        ],
      },
      {
        heading: 'Presentation always requires separate authorization',
        paragraphs: [
          'No profile is presented to any employer without a separate, mandate-specific authorization recorded against that mandate. The request will name the employer, or state clearly that disclosure is temporarily restricted and why.',
          'You may decline any authorization request. Your application remains open and your standing is unaffected.',
        ],
      },
      {
        heading: 'Withdrawal',
        paragraphs: [
          'Consent may be withdrawn from your portal privacy settings. Withdrawal takes effect immediately for future processing. Records of past consents are retained because they evidence what was authorized at the time.',
        ],
      },
    ],
  },
  {
    slug: 'cookie-policy',
    title: 'Cookie Policy',
    lastReviewed: '2026-08-01',
    summary: 'The cookies and similar technologies this platform uses, and how to control them.',
    requiresLegalReview: true,
    sections: [
      {
        heading: 'Strictly necessary',
        paragraphs: [
          'Used to maintain your authenticated session and to protect against cross-site request forgery. These cannot be disabled without preventing the portal from functioning.',
        ],
      },
      {
        heading: 'Preference',
        paragraphs: ['Used to remember interface choices such as saved filter state. Optional.'],
      },
      {
        heading: 'Analytics',
        paragraphs: [
          'Used in aggregate to understand how the public site is used. Set only with consent, and never on portal pages containing candidate information.',
        ],
      },
      {
        heading: 'Managing cookies',
        paragraphs: [
          'You may withdraw optional cookie consent at any time. Browser controls may also be used to block cookies, though strictly necessary cookies are required for sign-in.',
        ],
      },
    ],
  },
  {
    slug: 'accessibility-statement',
    title: 'Accessibility Statement',
    lastReviewed: '2026-08-01',
    summary: 'Our accessibility commitment, the standard we work to, and how to report a barrier.',
    requiresLegalReview: false,
    sections: [
      {
        heading: 'Standard',
        paragraphs: [
          'This platform is built to meet the Web Content Accessibility Guidelines version 2.2, Level AA. That standard is treated as an acceptance criterion for new work, not an afterthought.',
        ],
      },
      {
        heading: 'What that means in practice',
        paragraphs: [
          'Every interactive element is reachable and operable by keyboard, with a visible focus indicator. Form fields carry programmatic labels and errors are announced. Headings follow a correct hierarchy. Colour is never the sole carrier of meaning. Motion respects your operating-system reduced-motion preference.',
        ],
      },
      {
        heading: 'Known limitations',
        paragraphs: [
          'This section must list actual audited findings before publication and must not be published claiming full conformance without a completed audit.',
        ],
      },
      {
        heading: 'Reporting a barrier',
        paragraphs: [
          'If any part of this platform prevents you from completing what you came to do, contact us through the technical support form and we will respond with a specific remedy and timeframe. Reasonable accommodations are available at any stage of a recruitment process on request.',
        ],
      },
    ],
  },
  {
    slug: 'refund-policy',
    title: 'Refund Policy',
    lastReviewed: '2026-08-01',
    summary: 'Refund terms for optional, candidate-paid Executive Career Services.',
    requiresLegalReview: true,
    sections: [
      {
        heading: 'Scope',
        paragraphs: [
          'This policy applies only to Executive Career Services, which are optional and commissioned by you. It does not apply to retained executive search, which is commissioned and paid for by client organizations. Candidates are never charged for consideration against a mandate.',
        ],
      },
      {
        heading: 'Before work commences',
        paragraphs: ['A full refund is available at any point before the intake interview or first working session.'],
      },
      {
        heading: 'After work commences',
        paragraphs: [
          'Between commencement and first delivery, a partial refund is available reflecting the work completed, calculated against the stated scope.',
          'After first delivery no refund is available, though all included revision rounds remain available to you for the stated period.',
        ],
      },
      {
        heading: 'Scheduled sessions',
        paragraphs: [
          'Sessions cancelled more than forty-eight hours in advance are refunded in full. Inside forty-eight hours the session fee is retained.',
        ],
      },
      {
        heading: 'Service outcomes',
        paragraphs: [
          'No career service is sold on the basis of a recruitment outcome. Refunds are not available on the grounds that a service did not result in consideration, an interview, a presentation or an appointment, because no such outcome is ever promised.',
        ],
      },
    ],
  },
  {
    slug: 'recruitment-services-disclaimer',
    title: 'Recruitment Services Disclaimer',
    lastReviewed: '2026-08-01',
    summary: 'What we do and do not undertake as a retained executive search firm.',
    requiresLegalReview: true,
    sections: [
      {
        heading: 'We act for client organizations',
        paragraphs: [
          'We are engaged and paid by client organizations to conduct searches. We do not act as your agent and we do not charge candidates for consideration.',
        ],
      },
      {
        heading: 'No guarantee of any outcome',
        paragraphs: [
          'We do not guarantee employment, interviews, presentation to a client, compensation, or a job offer. We do not guarantee that any opportunity described on this platform will proceed, remain open, or be filled.',
        ],
      },
      {
        heading: 'Opportunity status labels',
        paragraphs: [
          'Verified indicates a commissioned mandate with a confirmed vacancy. Confidential Mandate indicates the same, with the client’s identity withheld at the client’s request. Proposed Opportunity indicates a requirement under discussion with no confirmed vacancy. Market-Matched Opportunity indicates a role category identified through research rather than a client instruction.',
          'Confidentiality is never used to obscure whether a vacancy is confirmed. The verification label and the disclosure label are always shown separately.',
        ],
      },
      {
        heading: 'Compensation information',
        paragraphs: [
          'Compensation figures are marked as either client-confirmed or estimated. Estimated figures are illustrative research observations and are not an offer, a commitment, or a representation of what any individual will be paid.',
        ],
      },
      {
        heading: 'Fraud notice',
        paragraphs: [
          'We never ask candidates for payment to be considered for a role, for banking details, for identity documents by email, or for account passwords. Any communication doing so is not from us. Report it through our contact page.',
        ],
      },
    ],
  },
];

export const getLegalDocument = (slug: string): LegalDocument | undefined =>
  legalDocuments.find((d) => d.slug === slug);
